import type { CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MarqueeProps {
    children: ReactNode
    label: string
    duration?: string
    reverse?: boolean
    className?: string
    setClassName?: string
    controlClassName?: string
}

// Endless horizontal scroller. `children` is one set of items; it is rendered
// twice so the loop is seamless. Pauses on hover, on focus, or with its button.
// With reduced motion it becomes a plain horizontal scroller.
export const Marquee: FC<MarqueeProps> = ({
    children,
    label,
    duration = '40s',
    reverse = false,
    className,
    setClassName,
    controlClassName
}): ReactNode => {
    const [paused, setPaused] = useState(false)

    return (
        <div className={cn('group/marquee relative', className)}>
            <div className='fade-x overflow-hidden motion-reduce:overflow-x-auto'>
                <div
                    style={{ '--marquee-duration': duration } as CSSProperties}
                    className={cn(
                        'flex w-max motion-safe:animate-marquee group-hover/marquee:[animation-play-state:paused] group-focus-within/marquee:[animation-play-state:paused]',
                        reverse && '[animation-direction:reverse]',
                        paused && '[animation-play-state:paused]'
                    )}
                >
                    <div className={cn('flex shrink-0', setClassName)}>
                        {children}
                    </div>

                    <div aria-hidden='true' className={cn('flex shrink-0 motion-reduce:hidden', setClassName)}>
                        {children}
                    </div>
                </div>
            </div>

            <button
                type='button'
                onClick={() => setPaused((value) => !value)}
                className={cn(
                    'absolute right-3 bottom-0 grid size-9 place-items-center rounded-full border-2 border-plum bg-cream text-plum shadow-[0_3px_0_0_var(--color-plum)] transition-transform hover:-translate-y-0.5 motion-reduce:hidden',
                    controlClassName
                )}
            >
                {paused ? <Play className='size-4' /> : <Pause className='size-4' />}

                <span className='sr-only'>
                    {paused ? `Play ${label}` : `Pause ${label}`}
                </span>
            </button>
        </div>
    )
}
